import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/Button';
import { Link } from 'react-router-dom';

const steps = [
  {
    id: '01',
    title: 'Upload Lab Results',
    description: 'Simply drag and drop your laboratory results files into our system using our intuitive interface.',
  },
  {
    id: '02',
    title: 'Automated Processing',
    description: 'Our AI algorithms analyze the microbiology data to identify pathogens and detect antimicrobial resistance patterns.',
  },
  {
    id: '03',
    title: 'Clinical Analysis',
    description: 'The system generates evidence-based treatment recommendations and complexity assessments based on the findings.',
  },
  {
    id: '04',
    title: 'Interactive Reporting',
    description: 'Access comprehensive, interactive reports with detailed clinical insights through our modern interface.',
  },
];

const HowItWorks: React.FC = () => {
  return (
    <div id="how-it-works" className="py-16 bg-section-light overflow-hidden">
      <div className="relative max-w-container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative">
          <div className="lg:text-center">
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">How It Works</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-text sm:text-4xl">
              Simple, Powerful, Effective
            </p>
            <p className="mt-4 max-w-2xl text-xl text-text lg:mx-auto">
              ReasonShield streamlines the laboratory results processing workflow, from data upload to comprehensive clinical reporting.
            </p>
          </div>

          <div className="mt-12">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
              {steps.map((step, index) => (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="relative bg-background p-6 rounded-lg shadow-md"
                >
                  <div className="absolute -top-4 -left-4 bg-primary rounded-full w-12 h-12 flex items-center justify-center text-text-light font-bold">
                    {step.id}
                  </div>
                  <h3 className="text-lg font-medium text-text mt-4">{step.title}</h3>
                  <p className="mt-2 text-base text-text">{step.description}</p>
                  
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 transform translate-x-1/2 -translate-y-1/2">
                      <svg className="h-6 w-6 text-primary" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M13.92 12l-4.95 4.95 1.41 1.41L16.34 12 10.38 6.04 8.97 7.45z" />
                      </svg>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
          
          <div className="mt-12 text-center">
            <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            viewport={{ once: true }}
            >
            <Button
              href="/contact"
              variant="primary"
              size="lg"
              asChild={true}
            >
              <Link to="/contact">Get Started Today</Link>
            </Button>
            
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks; 